import React, {useState, useRef, useContext, useEffect} from 'react'
import {
    Box,
    Typography,
    TextField,
    Select,
    MenuItem,
    Button,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    Paper,
    CircularProgress, InputLabel, Container, Checkbox, Link, FormLabel,
} from '@mui/material'
import {useLocation, useNavigate} from 'react-router-dom'
import {
    useStripe,
    useElements,
    CardNumberElement,
    CardExpiryElement,
    CardCvcElement
} from '@stripe/react-stripe-js'
import AuthContext from '../contexts/AuthContext'
import CartContext from '../contexts/CartContext'
import {saveToSessionStorage, getFromSessionStorage} from "../functions/Crypto.tsx";
import {useTranslation} from "react-i18next"
import TextForm from "../components/TextForm.tsx";
import OrderContext from "../contexts/OrderContext.tsx";
import PayPalPayment from "../components/PayPalPayment.tsx";
import PaymentContext from "../contexts/PaymentContext.tsx";
import {SupportedLanguages} from "../models/Languages.ts";
import i18n from "i18next";
import {IOrder} from "../models/IOrder.ts";
import {getProductEffectivePrice} from "../functions/GetEffectivePrice.ts";
import SaleContext from "../contexts/SaleContext.tsx";
import DiscountCodeContext from "../contexts/DiscountCodeContext.tsx";

const secretKey = import.meta.env.VITE_SEESION_SECRET_KEY
const VITE_FRONTEND_BASE_URL = import.meta.env.VITE_FRONTEND_BASE_URL

const countryOptions = [
    {name: 'Australien', region: 'INT'},
    {name: 'Belgien', region: 'EU'},
    {name: 'Brasilien', region: 'INT'},
    {name: 'Chile', region: 'INT'},
    {name: 'Deutschland', region: 'DE'},
    {name: 'Finnland', region: 'EU'},
    {name: 'Frankreich', region: 'EU'},
    {name: 'Indien', region: 'INT'},
    {name: 'Irland', region: 'EU'},
    {name: 'Italien', region: 'EU'},
    {name: 'Japan', region: 'INT'},
    {name: 'Kanada', region: 'INT'},
    {name: 'Korea, Republik', region: 'INT'},
    {name: 'Kroatien', region: 'EU'},
    {name: 'Marokko', region: 'INT'},
    {name: 'Mexiko', region: 'INT'},
    {name: 'Neuseeland', region: 'INT'},
    {name: 'Niederlande', region: 'EU'},
    {name: 'Norwegen', region: 'EU'},
    {name: 'Polen', region: 'EU'},
    {name: 'Schweiz', region: 'EU'},
    {name: 'Slowenien', region: 'EU'},
    {name: 'Spanien', region: 'EU'},
    {name: 'Türkei', region: 'INT'},
    {name: 'USA', region: 'INT'},
    {name: 'Ungarn', region: 'EU'},
    {name: 'Vereinigtes Königreich', region: 'INT'},
    {name: 'Volksrepublik China', region: 'INT'},
]

const shippingCosts = [
    {region: 'DE', cost: 6},
    {region: 'EU', cost: 10},
    {region: 'INT', cost: 15},
]

const CARD_OPTIONS = {
    style: {
        base: {
            fontSize: '16px',
            color: '#1b391b',
            iconColor: '#1b391b', //
            '::placeholder': {
                color: '#72828e',
            },
        },
        invalid: {
            color: '#fa755a',
            iconColor: '#fa755a',
        },
    },
};

const getCardIcon = (brand: string) => {
    const iconMap: Record<string, string> = {
        visa: '/icons/visa.svg',
        mastercard: '/icons/mastercard.svg',
        amex: '/icons/amex.svg',
        discover: '/icons/discover.svg',
        jcb: '/icons/jcb.svg',
        diners: '/icons/diners.svg',
        unionpay: '/icons/unionpay.svg',
        unknown: '/icons/generic.svg',
    };

    const src = iconMap[brand] || iconMap.generic;
    return <img src={src} alt={brand} height={16}/>;
};

const
    CheckoutPage = () => {
        const navigate = useNavigate()
        const {t} = useTranslation()
        const location = useLocation()
        const currentLanguage: SupportedLanguages = i18n.language as SupportedLanguages;
        const {
            order,
            setOrder,
            setOrderId,
            createOrderMutation,
            updateOrderMutation,
            finishCheckoutMutation,
            sendConfirmationMail
        } = useContext(OrderContext)
        const {sales} = useContext(SaleContext)
        const {validateCode, markCodeUsed} = useContext(DiscountCodeContext)
        const {createStripePaymentIntent, createKlarnaPaymentIntent} = useContext(PaymentContext)
        const [isProcessing, setIsProcessing] = useState(false)
        const [errorMessage, setErrorMessage] = useState('')
        const {isLoggedIn} = useContext(AuthContext)
        const {selectedCurrency, rates} = useContext(OrderContext)
        const {cart, cancelReservationMutation} = useContext(CartContext)
        const [cardBrand, setCardBrand] = useState('unknown');
        const [creditCardHolder, setCreditCardHolder] = useState<string>('');
        const [differentCardHolder, setDifferentCardHolder] = useState<boolean>(false);
        const [discountCode, setDiscountCode] = useState<string>('');
        const [discounted, setDiscounted] = useState<boolean>(false);
        const [invalidDiscount, setInvalidDiscount] = useState<boolean>(false);
        const userLocale = navigator.language || 'en-US';
        const addressLabels = [`${t('NAME_LABEL_TEXT')}`, `${t('STREET_LABEL_TEXT')}`, `${t('ADDRESS2_LABEL_TEXT')}`, `${t('CITY_LABEL_TEXT')}`, `${t('REGION_LABEL_TEXT')}`, `${t('ZIP_CODE_LABEL_TEXT')}`]
        const addressFormData = {
            name: {value: order.shippingAddress.name, required: true},
            street: {value: order.shippingAddress.street, required: true},
            address2: {value: order.shippingAddress.address2, required: false},
            city: {value: order.shippingAddress.city, required: true},
            region: {value: order.shippingAddress.region, required: false},
            postalCode: {value: order.shippingAddress.postalCode, required: false},
        }
        const stripe = useStripe()
        const elements = useElements()
        const formRef = useRef<HTMLFormElement>(null)

        useEffect(() => {
            window.scrollTo(0, 0); // Scroll to top when page loads
        }, []);

        useEffect(() => {
            const handleRouteChange = () => {
                const nextPath = window.location.pathname;

                if (nextPath.includes('/klarna')) {
                    return;
                }

                if (nextPath !== location.pathname) {
                    cancelReservationMutation.mutate()
                }
            };
            return () => {
                handleRouteChange();
            };
        }, [location]);

        useEffect(() => {
            if (cart && cart.items) {
                const total = cart.items.reduce((sum, item) => {
                    if (!item || !item.product) {
                        return sum;
                    }
                    return sum + getProductEffectivePrice(item.product, sales) * item.quantity;
                }, 0);

                const cartItemsWithPrices = cart.items.map(item => {
                    return {
                        ...item,
                        price: getProductEffectivePrice(item.product, sales) * item.quantity
                    }
                });

                setOrder({...order, items: cartItemsWithPrices, totalPrice: total});
            }
        }, [cart, sales]);

        useEffect(() => {
            const savedOrder = getFromSessionStorage('order', secretKey)
            const savedDiscounted = getFromSessionStorage('discounted', secretKey)
            if (savedOrder) {
                setOrder(savedOrder)
            }
            if (savedDiscounted) {
                setDiscountCode(savedDiscounted)
            }
        }, []);

        // useEffect(() => {
        //     const checkKlarnaStatus = async () => {
        //         const newOrder: IOrder = await createOrUpdateOrder()
        //         const urlParams = new URLSearchParams(window.location.search)
        //         const clientSecret = urlParams.get("payment_intent_client_secret")
        //
        //         if (!clientSecret || !stripe) return
        //
        //         const {paymentIntent} = await stripe.retrievePaymentIntent(clientSecret)
        //
        //         if (paymentIntent?.status === "succeeded") {
        //             finishCheckout(newOrder)
        //         } else if (paymentIntent?.status === "processing") {
        //             setErrorMessage(t('ERROR_CONFIRMING_CARD_PAYMENT'))
        //         } else {
        //             setErrorMessage(t('ERROR_CONFIRMING_CARD_PAYMENT'))
        //         }
        //     }
        //     checkKlarnaStatus()
        // }, [stripe])

        const validateDiscountCode = async () => {
            const discount = await validateCode(discountCode)
            if (discount.discountPercent && !discounted) {
                setInvalidDiscount(false)
                let newOrder = order
                newOrder = ({...order, totalPrice: order.totalPrice * (1 - discount.discountPercent / 100)});
                setDiscounted(true)
                setOrder(newOrder)
            } else if (discounted) {
                setInvalidDiscount(false)
                return
            } else if (!discount.discountPercent) {
                setInvalidDiscount(true)
            }
        }

        const toMinorUnits = (amount: number, currency: string): number => {
            // Currencies without decimal subunits
            const zeroDecimalCurrencies = ['JPY', 'KRW', 'VND'];

            if (zeroDecimalCurrencies.includes(currency.toUpperCase())) {
                return Math.round(amount); // No *100
            }

            return Math.round(amount * 100); // Most currencies like EUR, USD, GBP
        };

        const fetchShippingCost = (region: string): number => {
            let totalQuantity: number = 0
            cart?.items.forEach(item => {
                totalQuantity += item.quantity
            })
            const factor: number = Math.floor(totalQuantity / 3) + 1

            if (totalQuantity === 1) {
                const product = cart?.items[0].product

                const allZeroShippingCosts = product?.shippingCosts.every(item => item.cost === 0)

                if (!allZeroShippingCosts) {
                    const regionShippingCost = product?.shippingCosts.find(item => item.region === region)
                    if (regionShippingCost) {
                        return regionShippingCost.cost
                    } else {
                        return 0
                    }
                } else {
                    const shipping = shippingCosts.find(item => item.region === region)
                    if (shipping) {
                        return shipping.cost
                    }
                }
            } else {
                let allProductsZeroShipping = true

                cart?.items.forEach(item => {
                    const product = item.product
                    const allZeroShippingCosts = product.shippingCosts.every(item => item.cost === 0)
                    if (!allZeroShippingCosts) {
                        allProductsZeroShipping = false
                        return
                    }
                })

                if (!allProductsZeroShipping) {
                    let totalShippingCost = 0
                    let totalQuantityZeroShippingCost = 0
                    cart?.items.forEach(item => {
                        const product = item.product
                        const regionShippingCost = product.shippingCosts.find(item => item.region === region)
                        if (regionShippingCost && regionShippingCost.cost !== 0) {
                            totalShippingCost += regionShippingCost.cost * item.quantity
                        } else {
                            totalQuantityZeroShippingCost += item.quantity
                        }
                    })
                    const shipping = shippingCosts.find(item => item.region === region)
                    const factor1 = Math.floor(totalQuantityZeroShippingCost / 3)
                    if (shipping) {
                        totalShippingCost += factor1 * shipping.cost
                        return totalShippingCost
                    }
                } else {
                    const shipping = shippingCosts.find(item => item.region === region)
                    if (shipping) {
                        return shipping.cost * factor
                    }
                }
            }
            return 0
        }

        const handleCountryChange = (e: any, child?: any) => {
            const {value} = e.target
            let newOrder = order
            if (value === '') {
                newOrder = {
                    ...newOrder,
                    shippingCost: 0,
                    shippingAddress: {...newOrder.shippingAddress, country: value}
                }
                setOrder(newOrder)
                return
            } else {
                const region = child.props['data-key']
                const shippingCost: number = fetchShippingCost(region)
                newOrder = {
                    ...newOrder,
                    shippingCost: shippingCost,
                    shippingAddress: {...newOrder.shippingAddress, country: value}
                }
                setOrder(newOrder)
            }
            saveToSessionStorage('order', newOrder, secretKey)
        }

        const handleAddressChange = (key: string, value: any) => {
            let newOrder = order
            newOrder = {...newOrder, shippingAddress: {...newOrder.shippingAddress, [key]: value}}
            setOrder(newOrder)
            saveToSessionStorage('order', newOrder, secretKey)
        }

        const handleEmailChange = (e: any) => {
            let newOrder = order
            newOrder = {
                ...newOrder,
                shippingAddress: {...newOrder.shippingAddress, email: e.target.value}
            }
            setOrder(newOrder)
            saveToSessionStorage('order', newOrder, secretKey)
        }

        const handlePaymentMethodChange = (e: React.ChangeEvent<HTMLInputElement>) => {
            let newOrder = order
            newOrder = {...newOrder, paymentMethod: e.target.value}
            setOrder(newOrder)
            saveToSessionStorage('order', newOrder, secretKey)
        }

        const handleSubmit = async (e: React.FormEvent) => {
            e.preventDefault()
            setErrorMessage('')
            if (order.paymentMethod === 'Kreditkarte') {
                await handleStripePayment()
            }
            if (order.paymentMethod === 'Klarna') {
                await handleKlarnaPayment()
            }
        }

        const createOrUpdateOrder = async () => {
            let updatedOrder: IOrder;
            try {
                if (!order._id) {
                    updatedOrder = await createOrderMutation.mutateAsync(order);
                } else {
                    updatedOrder = await updateOrderMutation.mutateAsync(order);
                }
                saveToSessionStorage('order', order, secretKey)
                return updatedOrder
            } catch (err) {
                console.error("Order creation/updating failed", err);
                throw new Error(t('ERROR_CREATING_ORDER'));
            }

        }

        const finishCheckout = async (order: IOrder) => {
            if (order._id) {
                cancelReservationMutation.mutate()
                finishCheckoutMutation.mutate(order._id)
                sendConfirmationMail(order._id, currentLanguage, order.shippingAddress.email)
                await markCodeUsed(discountCode)
                navigate(`/order/${order._id}`)
                setOrderId(undefined)
            }
        }

        const handleStripePayment = async () => {
            if (!stripe || !elements) {
                setErrorMessage('Stripe ist nicht initialisiert')
                return
            }
            const cardElement = elements.getElement(CardNumberElement)
            if (!cardElement) return

            setIsProcessing(true)
            try {
                const newOrder: IOrder = await createOrUpdateOrder()
                const totalInBaseCurrency = (order.totalPrice + order.shippingCost) * rates[selectedCurrency];
                const amount = toMinorUnits(totalInBaseCurrency, selectedCurrency);

                const response = await createStripePaymentIntent(amount, selectedCurrency.toLowerCase())
                if (!response.ok) {
                    throw new Error(t('ERROR_CREATING_STRIPE_PAYMENT_INTENT'))
                }
                const {clientSecret} = await response.json()
                const {error, paymentIntent} = await stripe.confirmCardPayment(clientSecret, {
                    payment_method: {
                        card: cardElement,
                        billing_details: {
                            name: creditCardHolder ? creditCardHolder : newOrder.shippingAddress.name,
                            email: newOrder.shippingAddress.email,
                        },
                    },
                })
                if (error) {
                    throw new Error(t('ERROR_CONFIRMING_CARD_PAYMENT'))
                }
                if (paymentIntent?.status === 'succeeded') {
                    finishCheckout(newOrder)
                }
            } catch (error: any) {
                setErrorMessage(error.message)
            } finally {
                setIsProcessing(false)
            }
        }

        const handleKlarnaPayment = async () => {
            if (!stripe || !elements) {
                setErrorMessage('Stripe ist nicht initialisiert')
                return
            }
            setIsProcessing(true)
            try {
                const newOrder: IOrder = await createOrUpdateOrder()
                const totalInBaseCurrency = (order.totalPrice + order.shippingCost) * rates[selectedCurrency];
                const amount = toMinorUnits(totalInBaseCurrency, selectedCurrency);

                const response = await createKlarnaPaymentIntent(amount, selectedCurrency.toLowerCase())
                if (!response.ok) {
                    throw new Error(t('ERROR_CREATING_STRIPE_PAYMENT_INTENT'))
                }
                const country = countryOptions.find((country) => country.name === newOrder.shippingAddress.country)
                const countryCode = country!.region
                const {clientSecret} = await response.json()
                await stripe.confirmKlarnaPayment(clientSecret, {
                    payment_method: {
                        billing_details: {
                            email: newOrder.shippingAddress.email,
                            address: {
                                line1: newOrder.shippingAddress.street,
                                city: newOrder.shippingAddress.city,
                                postal_code: newOrder.shippingAddress.postalCode,
                                country: countryCode,
                            }
                        },
                    },
                    return_url: `${VITE_FRONTEND_BASE_URL}/checkout`,
                })
            } catch (error: any) {
                setErrorMessage(error.message)
            } finally {
                setIsProcessing(false)
            }

        }

        return (
            <Container sx={{padding: '12px'}}>
                <Paper elevation={4} sx={{paddingTop: 4, paddingBottom: 4, margin: 'auto', maxWidth: '900px'}}>
                    <Container>
                        <Typography variant="h4" textAlign="left" gutterBottom>
                            {t('CHECKOUT_PAGE_HEADER_TEXT')}
                        </Typography>
                    </Container>

                    <form ref={formRef} onSubmit={handleSubmit}>
                        <TextForm formLabels={addressLabels} formData={addressFormData} onChange={handleAddressChange}
                                  formHeader={t('CHECKOUT_PAGE_ADDRESS_TEXT')}/>
                        <Container>
                            <FormControl fullWidth sx={{marginBottom: '12px', marginTop: '12px'}}>
                                <InputLabel id="country-label">{t('COUNTRY_LABEL_TEXT')}</InputLabel>
                                <Select
                                    label={t('COUNTRY_LABEL_TEXT')}
                                    name="country"
                                    value={order.shippingAddress.country}
                                    onChange={(e: any, child) => handleCountryChange(e, child)}
                                    required
                                >
                                    {Object(countryOptions).map((option: any) => (
                                        <MenuItem key={option.name} value={option.name} data-key={option.region}>
                                            {option.name}
                                        </MenuItem>
                                    ))}
                                </Select>
                            </FormControl>
                            {!isLoggedIn && order.paymentMethod !== 'Klarna' && (
                                <TextField
                                    label={t('EMAIL_TEXT')}
                                    name="email"
                                    fullWidth
                                    value={order.shippingAddress.email}
                                    onChange={handleEmailChange}
                                    sx={{marginBottom: '10px'}}
                                    required
                                />
                            )}

                            <Typography variant="h5" gutterBottom>
                                {t('PAYMENT_METHOD_TEXT')}
                            </Typography>
                            <FormControl fullWidth={true}>
                                <RadioGroup
                                    row
                                    value={order.paymentMethod}
                                    onChange={(e: any) => handlePaymentMethodChange(e)}
                                >
                                    <FormControlLabel value="PayPal" control={<Radio/>} label={t('PAYPAL_LABEL_TEXT')}/>
                                    <FormControlLabel value="Kreditkarte" control={<Radio/>}
                                                      label={t('CREDIT_CART_LABEL_TEXT')}/>
                                    {/*<FormControlLabel value="Klarna" control={<Radio/>}*/}
                                    {/*                  label={t('KLARNA_PAYMENT_LABEL_TEXT')}/>*/}
                                </RadioGroup>
                            </FormControl>

                            {order.paymentMethod === 'Kreditkarte' && (
                                <Box sx={{margin: '20px 0'}}>
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                size="small"
                                                checked={differentCardHolder}
                                                onChange={(e) => setDifferentCardHolder(e.target.checked)}
                                            />
                                        }
                                        label={t("DIFFERENT_CARD_HOLDER_TEXT")}
                                    />
                                    {differentCardHolder &&
                                        <TextField
                                            required={true}
                                            label={t('CARD_HOLDER_TEXT')}
                                            fullWidth={true}
                                            size="small"
                                            value={creditCardHolder}
                                            sx={{mt: 2}}
                                            onChange={(event) => setCreditCardHolder(event.target.value)}
                                        />
                                    }
                                    <Box sx={{pt: 2}}>
                                        <FormLabel>{t('CARD_NUMBER_TEXT')}</FormLabel>
                                        <Box sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            border: '1px solid #ccc',
                                            p: 1,
                                            borderRadius: 1
                                        }}>
                                            <Box sx={{display: 'flex', alignItems: 'center', mr: 1}}>
                                                {getCardIcon(cardBrand)}
                                            </Box>
                                            <Box sx={{flex: 1}}>
                                                <CardNumberElement
                                                    options={CARD_OPTIONS}
                                                    onChange={(e) => {
                                                        setCardBrand(e.brand);
                                                    }}
                                                />
                                            </Box>
                                        </Box>
                                    </Box>
                                    <Box sx={{display: 'flex', gap: 2, pt: 2}}>
                                        <Box sx={{flex: 2}}>
                                            <FormLabel>{t('EXPIRY_TEXT')}</FormLabel>
                                            <Box sx={{p: 1, border: '1px solid #ccc', borderRadius: 1}}>
                                                <CardExpiryElement options={CARD_OPTIONS}/>
                                            </Box>
                                        </Box>
                                        <Box sx={{flex: 1}}>
                                            <FormLabel>{t('CVC_TEXT')}</FormLabel>
                                            <Box sx={{p: 1, border: '1px solid #ccc', borderRadius: 1}}>
                                                <CardCvcElement options={CARD_OPTIONS}/>
                                            </Box>
                                        </Box>
                                    </Box>
                                </Box>
                            )}

                            <FormControlLabel
                                required={true}
                                control={<Checkbox/>}
                                label={
                                    <Typography component="span">
                                        {t("PRIVACY_POLICY_CONSENT")} {" "}
                                        <Link href="/privacy-policy">
                                            {t("PRIVACY_POLICY")}
                                        </Link>
                                    </Typography>
                                }
                            />

                            <FormControlLabel
                                required={true}
                                control={<Checkbox/>}
                                label={
                                    <Typography component="span">
                                        {t("GTC_CONSENT")} {" "}
                                        <Link href="/gtc">
                                            {t("GTC")}
                                        </Link>
                                        {" "}
                                        <Link href="/revocation">
                                            {t("REVOCATION")}
                                        </Link>
                                    </Typography>
                                }
                            />

                            <Box sx={{margin: '20px 0'}}>
                                <Typography>
                                    {t('PRICE_LABEL_TEXT')}: {new Intl.NumberFormat(userLocale, {
                                    style: 'currency',
                                    currency: selectedCurrency,
                                }).format(order.totalPrice * rates[selectedCurrency])}
                                </Typography>
                                <Typography>
                                    {t('SHIPPING_COST_LABEL_TEXT')}: {new Intl.NumberFormat(userLocale, {
                                    style: 'currency',
                                    currency: selectedCurrency,
                                }).format(order.shippingCost * rates[selectedCurrency])}
                                </Typography>
                                <Typography variant="h6">
                                    {t('CART_PAGE_SUM_TEXT')}: {new Intl.NumberFormat(userLocale, {
                                    style: 'currency',
                                    currency: selectedCurrency,
                                }).format((order.totalPrice + order.shippingCost) * rates[selectedCurrency])}
                                </Typography>
                            </Box>

                            <Box sx={{display: 'flex', gap: 2, pt: 2}}>
                                <Box sx={{flex: 2}}>
                                    <TextField
                                        size="small"
                                        label={t('DISCOUNT_CODE')}
                                        fullWidth={true}
                                        value={discountCode}
                                        onChange={(event) => setDiscountCode(event.target.value)}
                                        sx={{pb: 2}}
                                    />
                                </Box>
                                <Box sx={{flex: 1}}>
                                    <Button
                                        fullWidth
                                        onClick={async () => {
                                            await validateDiscountCode()
                                        }}
                                        variant="outlined"
                                        color="primary"
                                    >
                                        {t('USE_DISCOUNT_CODE')}
                                    </Button>
                                </Box>
                            </Box>

                            {invalidDiscount && (
                                <Typography color="error" sx={{marginBottom: '10px'}}>
                                    {t('INVALID_DISCOUNT')}
                                </Typography>
                            )}

                            {errorMessage && (
                                <Typography color="error" sx={{marginBottom: '10px'}}>
                                    {errorMessage}
                                </Typography>
                            )}

                            {order.paymentMethod === 'Kreditkarte' && (
                                <Button
                                    type="submit"
                                    variant="contained"
                                    color="primary"
                                    fullWidth
                                    disabled={isProcessing}
                                >
                                    {isProcessing ?
                                        <CircularProgress size={24}/> : `${t('CHECKOUT_PAGE_BUTTON_TEXT')}`}
                                </Button>
                            )}
                            {order.paymentMethod === 'PayPal' && (
                                <PayPalPayment
                                    formRef={formRef}
                                    onSubmit={async () => {
                                        if (discounted) await markCodeUsed(discountCode)
                                    }}
                                />
                            )}
                            {order.paymentMethod === 'Klarna' && (
                                <Box sx={{margin: '20px 0'}}>
                                    <TextField
                                        label={t('EMAIL_TEXT')}
                                        name="email"
                                        fullWidth
                                        value={order.shippingAddress.email}
                                        onChange={handleEmailChange}
                                        sx={{marginBottom: '10px'}}
                                    />
                                    {!isLoggedIn && (
                                        <Typography>
                                            *{t('KLARNA_PAYMENT_INFO_TEXT')}
                                        </Typography>
                                    )}
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        color="primary"
                                        fullWidth
                                        disabled={isProcessing}
                                    >
                                        {isProcessing ?
                                            <CircularProgress
                                                size={24}/> : `${t('CHECKOUT_PAGE_KLARNA_BUTTON_TEXT')}`}
                                    </Button>
                                </Box>
                            )}
                        </Container>
                    </form>
                </Paper>
            </Container>
        )
    }

export default CheckoutPage
